import { useEffect, useState } from 'react';
import axios from 'axios';
import { Youtube, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * LiveGoalHighlights — Onefootball-style per-goal video clips. Polls
 * `/api/live-scores/match/{id}/live-highlights` every 45s while the
 * match is in-play. Each goal event tries to surface a Sky/TNT/BBC
 * YouTube clip; if none exists yet (broadcasters are slow to post the
 * first 30s after a goal) we render a "Search on YouTube" fallback that
 * opens externally.
 *
 * Only renders when the match has at least one goal AND we're inside an
 * in-play status. Hidden state has zero footprint on the page.
 */
const LiveGoalHighlights = ({ fixtureId, status, goalCount = 0 }) => {
  const [data, setData] = useState(null);
  const isLive = ['1H', '2H', 'ET', 'P', 'HT', 'BT'].includes(status);
  const shouldFetch = isLive && goalCount > 0 && fixtureId;

  useEffect(() => {
    if (!shouldFetch) return undefined;
    let alive = true;
    const fetchClips = async () => {
      try {
        const { data: d } = await axios.get(`${API}/live-scores/match/${fixtureId}/live-highlights`);
        if (alive) setData(d);
      } catch {
        if (alive) setData({ enabled: false, clips: [] });
      }
    };
    fetchClips();
    // Poll every 45s — broadcasters typically post goal clips within 1-3 min
    const interval = setInterval(fetchClips, 45000);
    return () => { alive = false; clearInterval(interval); };
  }, [fixtureId, goalCount, shouldFetch]);

  if (!isLive || goalCount === 0) return null;
  if (!data || !data.enabled) return null;
  const clips = (data.clips || []).filter((c) => c.scorer);
  if (clips.length === 0) return null;

  return (
    <div className="rounded-2xl bg-[var(--sk-surface)] p-4" data-testid="live-goal-highlights">
      <div className="flex items-center gap-1.5 mb-3">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
        </span>
        <div className="text-[10px] font-black uppercase tracking-wider text-rose-300">Goal clips · Live</div>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {clips.slice().reverse().map((c, i) => (
          <ClipRow key={`${c.minute}-${c.scorer}-${i}`} clip={c} />
        ))}
      </div>
      <p className="mt-2 text-[10px] text-zinc-600">
        Clips appear as broadcasters post them — usually 1–3 min after a goal.
      </p>
    </div>
  );
};

const ClipRow = ({ clip }) => {
  const { video, scorer, minute, team, search_url } = clip;
  if (video && video.video_id) {
    return (
      <a
        href={`https://www.youtube.com/watch?v=${video.video_id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex gap-3 p-2 rounded-lg bg-black/30 hover:bg-black/40 transition-colors border border-white/5"
        data-testid={`live-clip-${scorer.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="relative w-20 h-12 rounded-md overflow-hidden bg-zinc-900 shrink-0">
          {video.thumbnail && (
            <img src={video.thumbnail} alt={video.title} className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <Youtube className="w-5 h-5 text-white drop-shadow-lg" />
          </div>
        </div>
        <div className="min-w-0 flex-1 flex flex-col justify-center">
          <div className="text-xs font-bold text-white truncate">
            {minute}' {scorer}
          </div>
          <div className="text-[10px] text-zinc-400 truncate">{team}</div>
          <div className="text-[10px] text-zinc-500 truncate">{video.channel}</div>
        </div>
      </a>
    );
  }
  // Fallback row — no clip on YouTube yet
  return (
    <a
      href={search_url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-2 rounded-lg bg-black/20 hover:bg-black/30 transition-colors border border-dashed border-white/10"
      data-testid={`live-clip-pending-${scorer.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="w-8 h-8 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center shrink-0">
        <span className="text-[10px] font-extrabold text-rose-300">{minute}'</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-bold text-white truncate">{scorer}</div>
        <div className="text-[10px] text-zinc-500 truncate">{team} · clip pending</div>
      </div>
      <ExternalLink className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
    </a>
  );
};

export default LiveGoalHighlights;
